/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateFormat } from '@gh/core-data';
import { Type } from '@gh/core-type';
import {
  InlineTimepickerComponent,
  KEY_DOWN,
  KEY_END,
  KEY_HOME,
  KEY_LEFT,
  KEY_RIGHT,
  KEY_TAB,
  KEY_UP,
} from '@gh/core-ui';
import { isDateInvalid, isElementFocused, mergeDateAndTime, nullate, splitDateAndTime } from '@gh/core-util';
import { interval$, of$ } from '@gh/rx';
import { concat$, first$, mapTo$, switchMap$ } from '@gh/rx/operators';
import { Column, ICellEditorParams, RowNode } from 'ag-grid-community';
import { defer, includes, isEmpty, isFunction, isNil, stubTrue } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { CellParams, EditableCellModel } from '../../../state';
import { GridCellEditorComponent } from '../../grid-component.interfaces';

const STOPPED_KEYS = [KEY_DOWN, KEY_UP, KEY_LEFT, KEY_RIGHT, KEY_HOME, KEY_END];

const getDateFromGroup = (group: FormGroup, hasDate: boolean, hasTime: boolean) => {
  const { value } = group;
  if (isEmpty(value) || isNil(value.date) && hasDate || isNil(value.time) && hasTime) {
    return void 0;
  } else if (hasDate && isDateInvalid(value.date) || hasTime && isDateInvalid(value.time)) {
    return new Date('Invalid Date');
  } else if (!hasDate) {
    return value.time;
  } else if (!hasTime) {
    return value.date;
  } else {
    return mergeDateAndTime(value.date, value.time);
  }
};

@Component({
  selector: 'gh-grid-date-time-cell-editor',
  template: require('./grid-date-time-cell-editor.component.html'),
  styles: [require('./grid-input-cell-editor.component.scss'), require('./grid-date-time-cell-editor.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridDateTimeCellEditorComponent implements GridCellEditorComponent, AfterViewInit, OnDestroy {
  required: boolean = false;

  group: FormGroup;
  editorOptions?: any;

  @ViewChild('dateInput', { static: false }) dateInputEl: ElementRef;
  @ViewChild(InlineTimepickerComponent, { static: false }) timeInput: InlineTimepickerComponent;
  @ViewChild(MatDatepicker, { static: false }) datepicker: MatDatepicker<Date>;

  hasDate: boolean;
  hasTime: boolean;

  type?: Type<any>;

  // This stream allows to block outside clicks temporarily.
  // This is necessary because sometimes for some very small period of time datepicker panel looses focus:
  // this is happened when user clicks on item in multi-year/year views
  // (GridExtFeature detects that grid loose focus and calls stopEditing manually)
  temporaryIgnoreOutsideClicks$: Observable<boolean>;

  private _isDateAllowed: PredicateFn2<CellParams, Date>;
  private _isTimeAllowed: PredicateFn2<CellParams, Date>;
  private _cursorOnFocus?: 'start' | 'end';

  private node: RowNode;
  private column: Column;
  private stopEditing: () => void;
  private focusTimeout: number;
  private temporaryIgnoreOutsideClicks$$ = new BehaviorSubject<boolean>(false);

  constructor(private formBuilder: FormBuilder) {
    this.isDateAllowed = this.isDateAllowed.bind(this);
    this.isTimeAllowed = this.isTimeAllowed.bind(this);
  }

  ngAfterViewInit(): void {
    this.focusTimeout = defer(() => {
      if (this.hasDate) {
        if (this._cursorOnFocus && this._cursorOnFocus  === 'start') {
          this.dateInputEl.nativeElement.setSelectionRange(0, 0, 'backward');
        }
        this.dateInputEl.nativeElement.focus();
      } else if (this.hasTime) {
        this.timeInput.focus();
      }
      this.focusTimeout = 0;
    });

    if (!this.hasTime) {
      this.datepicker.stateChanges.subscribe(() => this.stopEditing());
    }
  }

  ngOnDestroy(): void {
    this.temporaryIgnoreOutsideClicks$$.complete();
    clearTimeout(this.focusTimeout);
  }

  agInit(params: ICellEditorParams): void {
    const model: EditableCellModel = params['modelFactory']({
      node: params.node,
      colDef: params.column.getColDef(),
      data: params.node.data,
    });

    const typeOrFactory = params['type'];

    this.node = params.node;
    this.column = params.column;
    this.required = model.required;
    this.type = isFunction(typeOrFactory) ? typeOrFactory(params.node.data) : typeOrFactory;
    this.stopEditing = params.stopEditing;
    this.editorOptions = params['options'];
    this._cursorOnFocus = params['cursorOnFocus'];
    this._isDateAllowed = params['isDateAllowed'] || stubTrue;
    this._isTimeAllowed = params['isTimeAllowed'] || stubTrue;

    const format = params['format'] || DateFormat.DateTime;
    this.hasDate = format !== DateFormat.Time;
    this.hasTime = format !== DateFormat.Date;

    const date = model.value && !isDateInvalid(model.value) ? splitDateAndTime(model.value) : void 0;
    this.group = this.formBuilder.group(
      {
        date: this.formBuilder.control(<any>(date ? date.date : void 0), this.getDateComponentFormValidator(model)),
        time: this.formBuilder.control(<any>(date ? date.time : void 0), this.getDateComponentFormValidator(model)),
      },
      {
        validator: this.getDateGroupFormValidator(model),
      });
    const { charPress } = params;
    if (charPress) {
      this.group.setValue({ date: void 0, time: void 0 });
    }

    this.temporaryIgnoreOutsideClicks$ = this.temporaryIgnoreOutsideClicks$$.pipe(
      // ignore outside clicks for some small time to allow datepicker to set focus correctly
      switchMap$((ignore) =>
        ignore
          // tslint:disable-next-line:no-magic-numbers
          ? of$(true).pipe(concat$(interval$(10).pipe(first$(), mapTo$(false))))
          : [false]));
  }

  getValue(): any {
    return getDateFromGroup(this.group, this.hasDate, this.hasTime);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (includes(STOPPED_KEYS, event.keyCode)) {
      event.stopPropagation();
    }
    if (this.hasDate && isElementFocused(this.dateInputEl.nativeElement) && event.keyCode === KEY_DOWN) {
      this.datepicker.open();
      event.preventDefault();
    }

    if (this.hasDate && this.hasTime) {
      if (event.shiftKey && event.keyCode === KEY_TAB && this.timeInput.focused) {
        event.stopPropagation();
        event.preventDefault();
        this.dateInputEl.nativeElement.focus();
      } else if (!event.shiftKey && event.keyCode === KEY_TAB && isElementFocused(this.dateInputEl.nativeElement)) {
        event.stopPropagation();
        event.preventDefault();
        this.timeInput.focus();
      }
    }
  }

  onParentDateSelected(): void {
    this.temporaryIgnoreOutsideClicks$$.next(true);
  }

  isDateAllowed(date: Date): boolean {
    return this._isDateAllowed(this.createCellParams(), date);
  }

  isTimeAllowed(date: Date): boolean {
    return this._isTimeAllowed(this.createCellParams(), date);
  }

  private createCellParams(): CellParams {
    return { node: this.node, colDef: this.column.getColDef(), data: this.node.data };
  }

  private getDateGroupFormValidator(model: EditableCellModel): ValidatorFn {
    return (group: FormGroup) => {
      const date = getDateFromGroup(group, this.hasDate, this.hasTime);
      return nullate(isNil(date) || isDateInvalid(date) ? void 0 : model.validate(date));
    };
  }

  private getDateComponentFormValidator(model: EditableCellModel): ValidatorFn {
    return (control: AbstractControl) => {
      const { value } = control;
      // validate if current date part is required
      if (isNil(value)) {
        if (model.required) {
          return { required: true };
        } else {
          return nullate(void 0);
        }
      }
      // validate format of current date part
      if (isDateInvalid(control.value)) {
        return { format: { messageId: 'msg_field_invalid_format' } };
      }
      return nullate(void 0);
    };
  }
}
